import type {
  SelectionList,
  TreeItem,
  ValidValueTypes,
} from '@revelio/iso-utility';
import { SelectionCategories } from '@revelio/iso-utility';

import { resolveTreeSelectionFromFilterData } from '../CustomDash/filters/ai-search/transformAiResponseToCustomDashFilters/selection';
import type { FilterDataResult } from '../CustomDash/filters/contexts/FilterProvider';
import type {
  FilterId,
  ListSelection,
  TreeSelection,
} from '../CustomDash/filters/types/filters';
import { FilterId as FilterIdEnum } from '../CustomDash/filters/types/filters';
import { findMwCustomSkillListItemByName } from './find-mw-custom-skill-list-item';
import type { GlobalAssistantReportResolvedSelection } from './global-ai-assistant-api';

const LEVEL_ID_TO_MW_SELECTION_LIST_ALIASES: Record<string, string[]> = {
  msa: [SelectionCategories.METRO_AREA, SelectionCategories.FULL_MSA],
  metro_area: [SelectionCategories.MSA],
  full_msa: [SelectionCategories.METRO_AREA],
};

const extractMwBreadcrumbItemId = (resolvedId: string): string => {
  const afterLastDot = resolvedId.includes('.')
    ? (resolvedId.split('.').pop() ?? resolvedId)
    : resolvedId;
  return afterLastDot.includes('_')
    ? (afterLastDot.split('_').pop() ?? afterLastDot)
    : afterLastDot;
};

export const toTreeSelectionFromResolved = (
  resolved: GlobalAssistantReportResolvedSelection
): TreeSelection => ({
  id: resolved.id,
  name: resolved.name,
  levelId: resolved.levelId as TreeSelection['levelId'],
  isCurrent: true,
});

const parseResolvedTreeLookup = (
  resolved: GlobalAssistantReportResolvedSelection
): { selectionListId: string; numericId: number } | null => {
  if (resolved.levelId === 'company' && resolved.rcid !== undefined) {
    return { selectionListId: 'company', numericId: resolved.rcid };
  }

  if (resolved.id.includes('.')) {
    const [prefix, suffix] = resolved.id.split('.');
    const numericId = Number(suffix);
    if (prefix && suffix && !Number.isNaN(numericId)) {
      return { selectionListId: prefix, numericId };
    }
  }

  const leafId = resolved.id.includes('_')
    ? (resolved.id.split('_').pop() ?? resolved.id)
    : resolved.id;
  const numericId = Number(leafId);
  if (!Number.isNaN(numericId)) {
    return { selectionListId: resolved.levelId, numericId };
  }

  return null;
};

export const toPromotedReportTreeSelectionFromResolved = ({
  resolved,
  filterId,
  getFilterData,
}: {
  resolved: GlobalAssistantReportResolvedSelection;
  filterId: FilterId;
  getFilterData: (filterId: FilterId) => FilterDataResult;
}): TreeSelection => {
  const lookup = parseResolvedTreeLookup(resolved);
  if (lookup) {
    const treeResult = resolveTreeSelectionFromFilterData({
      filterId,
      selectionListId: lookup.selectionListId,
      numericId: lookup.numericId,
      getFilterData,
    });
    if (treeResult) {
      return {
        id: treeResult.id,
        name: treeResult.name,
        levelId: lookup.selectionListId as TreeSelection['levelId'],
        isCurrent: true,
      };
    }
  }

  return toTreeSelectionFromResolved(resolved);
};

export const toListSelectionFromResolved = (
  resolved: GlobalAssistantReportResolvedSelection
): ListSelection => ({
  id: resolved.id,
  name: resolved.name,
});

export const usesMwCustomSkillSelectionLists = (
  selectionLists: string[]
): boolean =>
  selectionLists.some((listId) =>
    String(listId).toLowerCase().includes('custom_skill')
  );

export const toMwCustomSkillTreeSelectionsFromResolved = (
  resolved: GlobalAssistantReportResolvedSelection,
  customSkillLists: SelectionList<ValidValueTypes>[]
): Record<string, TreeItem<string>> => {
  const matched = findMwCustomSkillListItemByName(
    customSkillLists,
    resolved.name
  );
  if (!matched) {
    return {};
  }

  const displayName =
    matched.ref.longName ?? matched.ref.shortName ?? resolved.name;
  const treeItemKey = `${matched.listId}.${matched.ref.id}`;

  return {
    [treeItemKey]: {
      id: matched.ref.id,
      item: {
        id: matched.ref.id,
        shortName: matched.ref.shortName ?? displayName,
        longName: matched.ref.longName ?? displayName,
        label: displayName,
      },
      children: [],
      parentId: undefined,
      selectionListId: matched.listId as TreeItem<string>['selectionListId'],
    },
  };
};

export const toMwTreeSelectionsFromResolved = (
  resolved: GlobalAssistantReportResolvedSelection,
  selectionListId: string,
  selectionLists: string[] = [],
  customSkillLists: SelectionList<ValidValueTypes>[] | null = null,
  getFilterData?: (filterId: FilterId) => FilterDataResult
): Record<string, TreeItem<string>> => {
  if (
    usesMwCustomSkillSelectionLists(selectionLists) &&
    customSkillLists &&
    customSkillLists.length > 0
  ) {
    return toMwCustomSkillTreeSelectionsFromResolved(
      resolved,
      customSkillLists
    );
  }

  if (usesMwCustomSkillSelectionLists(selectionLists)) {
    return {};
  }

  const isCompanyWithRcid =
    resolved.levelId === 'company' && resolved.rcid !== undefined;
  if (!isCompanyWithRcid && selectionLists.length > 0) {
    const levelIsInColumn = selectionLists.includes(resolved.levelId);
    const aliasMatch =
      LEVEL_ID_TO_MW_SELECTION_LIST_ALIASES[resolved.levelId]?.some((alias) =>
        selectionLists.includes(alias)
      ) ?? false;
    if (!levelIsInColumn && !aliasMatch) {
      return {};
    }
  }

  if (isCompanyWithRcid) {
    const treeId = `${selectionListId}.${resolved.rcid}`;
    return {
      [treeId]: {
        id: treeId,
        item: {
          id: String(resolved.rcid),
          shortName: resolved.name,
          longName: resolved.name,
          label: resolved.name,
          rcid: resolved.rcid,
        },
        children: [],
        parentId: undefined,
        selectionListId: selectionListId as TreeItem<string>['selectionListId'],
      },
    };
  }

  const lookup = parseResolvedTreeLookup(resolved);
  if (lookup && getFilterData) {
    const filterId = (() => {
      if (lookup.selectionListId.startsWith('skill')) {
        return FilterIdEnum.CUSTOM_SKILL;
      }
      if (lookup.selectionListId.startsWith('role')) {
        return FilterIdEnum.ROLE;
      }
      if (lookup.selectionListId === SelectionCategories.COMPANY) {
        return FilterIdEnum.COMPANY;
      }
      return FilterIdEnum.GEOGRAPHY;
    })();

    const treeResult = resolveTreeSelectionFromFilterData({
      filterId,
      selectionListId: lookup.selectionListId,
      numericId: lookup.numericId,
      getFilterData,
    });
    if (treeResult) {
      const treeId = treeResult.id.includes('.')
        ? treeResult.id
        : `${lookup.selectionListId}.${treeResult.id.split('_').pop() ?? treeResult.id}`;
      return {
        [treeId]: {
          id: treeId,
          item: {
            id: treeResult.id.split('_').pop() ?? treeResult.id,
            shortName: treeResult.name,
            longName: treeResult.name,
            label: treeResult.name,
          },
          children: [],
          parentId: undefined,
          selectionListId:
            lookup.selectionListId as TreeItem<string>['selectionListId'],
        },
      };
    }
  }

  const rawNumericId = extractMwBreadcrumbItemId(resolved.id);
  const resolvedSelectionListId = (selectionListId ||
    resolved.levelId) as TreeItem<string>['selectionListId'];
  const treeKey = `${resolvedSelectionListId}.${rawNumericId}`;

  return {
    [treeKey]: {
      id: rawNumericId,
      item: {
        id: rawNumericId,
        shortName: resolved.name,
        longName: resolved.name,
        label: resolved.name,
      },
      children: [],
      parentId: undefined,
      selectionListId: resolvedSelectionListId,
    },
  };
};

export const toMwListSelectionsFromResolved = (
  resolved: GlobalAssistantReportResolvedSelection,
  selectionListId: string
): Record<string, TreeItem<string>> => {
  const treeId = resolved.id.includes('.')
    ? resolved.id
    : `${selectionListId}.${resolved.id}`;

  const itemId = extractMwBreadcrumbItemId(resolved.id);

  return {
    [treeId]: {
      id: itemId,
      item: {
        id: itemId,
        shortName: resolved.name,
        longName: resolved.name,
        label: resolved.name,
      },
      children: [],
      parentId: undefined,
      selectionListId: selectionListId as TreeItem<string>['selectionListId'],
    },
  };
};

export const getMwSelectionListId = (
  selectionLists: string[],
  levelId?: string
): string => {
  if (levelId && selectionLists.includes(levelId)) {
    return levelId;
  }
  if (levelId) {
    const aliases = LEVEL_ID_TO_MW_SELECTION_LIST_ALIASES[levelId] ?? [];
    const aliasMatch = aliases.find((alias) => selectionLists.includes(alias));
    if (aliasMatch) {
      return aliasMatch;
    }
  }
  return (
    selectionLists.find((list) => list === SelectionCategories.COMPANY) ??
    selectionLists[0] ??
    SelectionCategories.COMPANY
  );
};

export const isListLevelSelection = (levelId: string): boolean =>
  levelId === 'seniority';
